import { Box, Button, ChevronDownOutline, forwardRef, rem, StyleProps, Text } from '@bonusly/design-system';

import { useHomepageRedesignEnabled } from '../../../../modules/bonus-feed/features/recognition/components/BonusFeed/hooks/useHomepageRedesignEnabled';
import { usePrivacy } from './hooks/use-privacy';

type PrivacyButtonProps = StyleProps;

export const PrivacyButton = forwardRef<PrivacyButtonProps, 'button'>((props, ref) => {
	const { selectedOption, buttonEnabled } = usePrivacy();
	const isHomepageRedesignEnabled = useHomepageRedesignEnabled();

	// Redesigned compact button for homepage redesign
	if (isHomepageRedesignEnabled) {
		return (
			<Box
				as="button"
				type="button"
				disabled={!buttonEnabled}
				display="inline-flex"
				alignItems="center"
				gap={rem(4)}
				cursor={buttonEnabled ? 'pointer' : 'not-allowed'}
				opacity={buttonEnabled ? 1 : 0.6}
				aria-label={selectedOption.description}
				h={rem(24)}
				px={rem(6)}
				py={rem(2)}
				borderRadius={rem(8)}
				bg="secondary.bgAlpha"
				_hover={{ bg: buttonEnabled ? 'secondary.bgAlphaHover' : 'secondary.bgAlpha' }}
				_focus={{ outline: '2px solid', outlineColor: 'primary.fg', outlineOffset: '2px' }}
				data-testid="privacy-button"
				ref={ref}
				{...props}
			>
				{selectedOption.icon}
				<Text fontSize={rem(14)} color="text.soft" whiteSpace="nowrap">
					{selectedOption.label}
				</Text>
				<ChevronDownOutline boxSize={3} color="text.soft" />
			</Box>
		);
	}

	// Original button design
	return (
		<Button
			aria-label={selectedOption.description}
			borderRadius={rem(22)}
			borderStyle="solid"
			borderWidth="1px"
			colorScheme="secondary"
			h={{ base: rem(36), xs: rem(44) }}
			variant="ghost"
			isDisabled={!buttonEnabled}
			ref={ref}
			data-testid="privacy-button"
			{...props}
		>
			{selectedOption.icon}
			<Box
				display={{
					base: 'none',
					md: 'block',
				}}
				ml={1}
			>
				{selectedOption.label}
			</Box>
		</Button>
	);
});
